import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth-context';
import { 
  Search, Store, ShoppingCart, Plus, Minus, Trash2, 
  MapPin, Phone, ChevronLeft, Loader2, Package, Truck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageSeo } from '@/components/seo/PageSeo';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { supermarket } from '@/lib/api';

const SupermarketStorePage = () => {
  const { sellerId } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Checkout form
  const [checkoutForm, setCheckoutForm] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    delivery_address: '',
    notes: ''
  });

  const isTurkish = i18n.language === 'tr';

  useEffect(() => {
    fetchData();
  }, [sellerId]);

  useEffect(() => {
    if (user) {
      setCheckoutForm(prev => ({
        ...prev,
        customer_name: user.name || '',
        customer_email: user.email || ''
      }));
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [storesRes, productsRes] = await Promise.all([
        supermarket.getStores(),
        supermarket.getStoreProducts(sellerId, { limit: 200 })
      ]);
      
      const storeData = storesRes.data?.stores?.find(s => s.seller_id === sellerId);
      setStore(storeData);
      setProducts(productsRes.data?.products || []);
    } catch (error) {
      console.error('Failed to fetch store data:', error);
      toast.error(isTurkish ? 'Mağaza yüklenemedi' : 'Failed to load store');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const params = { limit: 200 };
      if (searchQuery) params.search = searchQuery;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      
      const res = await supermarket.getStoreProducts(sellerId, params);
      setProducts(res.data?.products || []);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(handleSearch, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory]);

  // Cart functions
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success(isTurkish ? 'Sepete eklendi' : 'Added to cart');
  };

  const updateCartQuantity = (productId, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === productId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryFee = cartTotal >= 100 ? 0 : 15;
  const orderTotal = cartTotal + deliveryFee;

  const handleCheckout = async () => {
    if (!checkoutForm.customer_name || !checkoutForm.customer_phone || !checkoutForm.delivery_address) {
      toast.error(isTurkish ? 'Lütfen tüm alanları doldurun' : 'Please fill all fields');
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        seller_id: sellerId,
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity
        })),
        ...checkoutForm
      };

      const res = await supermarket.createOrder(orderData);
      toast.success(isTurkish ? 'Siparişiniz oluşturuldu!' : 'Order placed successfully!');
      setCart([]);
      setCheckoutOpen(false);
      setCartOpen(false);
      
      // Navigate to order confirmation
      navigate(`/supermarkets/order/${res.data.order.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isTurkish ? 'Sipariş oluşturulamadı' : 'Failed to place order'));
    } finally {
      setSubmitting(false);
    }
  };

  const categoryNames = {
    gida: isTurkish ? 'Temel Gıda' : 'Food & Groceries',
    sut_urunleri: isTurkish ? 'Süt Ürünleri' : 'Dairy',
    icecekler: isTurkish ? 'İçecekler' : 'Beverages',
    atistirmalik: isTurkish ? 'Atıştırmalıklar' : 'Snacks',
    temizlik: isTurkish ? 'Temizlik' : 'Cleaning',
    kisisel_bakim: isTurkish ? 'Kişisel Bakım' : 'Personal Care',
    bebek: isTurkish ? 'Bebek' : 'Baby',
    ev_gerecleri: isTurkish ? 'Ev Gereçleri' : 'Home'
  };

  // Get unique categories from products
  const availableCategories = [...new Set(products.map(p => p.category))];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-48 w-full rounded-2xl mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <Skeleton key={i} className="h-48 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Store className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {isTurkish ? 'Mağaza bulunamadı' : 'Store not found'}
          </h2>
          <Button onClick={() => navigate('/supermarkets')} variant="outline">
            <ChevronLeft className="w-4 h-4 mr-2" />
            {isTurkish ? 'Geri Dön' : 'Go Back'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="supermarket-store-page">
      <PageSeo
        title={store.seller_name}
        description={t('supermarket.heroSubtitle')}
        segments={['supermarkets', 'store', sellerId]}
      />
      {/* Store Header */}
      <section className="bg-gradient-to-r from-emerald-600 to-emerald-800 text-white py-8">
        <div className="container mx-auto px-4">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white mb-4"
            onClick={() => navigate('/supermarkets')}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {isTurkish ? 'Tüm Süpermarketler' : 'All Supermarkets'}
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Store className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{store.seller_name}</h1>
              <p className="text-white/80">
                {store.product_count} {isTurkish ? 'ürün' : 'products'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="sticky top-[64px] z-30 bg-white border-b shadow-sm py-4">
        <div className="container mx-auto px-4">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="search"
                placeholder={isTurkish ? 'Ürün ara...' : 'Search products...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11 rounded-full"
              />
            </div>
            
            {/* Cart Button */}
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button className="relative rounded-full" size="lg">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {isTurkish ? 'Sepet' : 'Cart'}
                  {cart.length > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center bg-red-500">
                      {cart.reduce((sum, item) => sum + item.quantity, 0)}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    {isTurkish ? 'Sepetim' : 'My Cart'}
                  </SheetTitle>
                </SheetHeader>
                
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <ShoppingCart className="w-12 h-12 mb-4" />
                    <p>{isTurkish ? 'Sepetiniz boş' : 'Your cart is empty'}</p>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-auto py-4 space-y-4">
                      {cart.map((item) => (
                        <div key={item.id} className="flex gap-3 p-3 bg-secondary/50 rounded-lg">
                          <div className="w-16 h-16 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                            <span className="text-xl font-bold text-emerald-600">
                              {item.brand?.charAt(0)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm line-clamp-1">{item.name}</p>
                            <p className="text-xs text-muted-foreground">{item.brand}</p>
                            <p className="text-sm font-semibold text-emerald-600">
                              ₺{(item.price * item.quantity).toFixed(2)}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => removeFromCart(item.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateCartQuantity(item.id, -1)}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="w-8 text-center text-sm font-medium">
                                {item.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateCartQuantity(item.id, 1)}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <Separator className="my-4" />
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span>{isTurkish ? 'Ara Toplam' : 'Subtotal'}</span>
                        <span>₺{cartTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>{isTurkish ? 'Teslimat' : 'Delivery'}</span>
                        <span className={deliveryFee === 0 ? 'text-green-600' : ''}>
                          {deliveryFee === 0 ? (isTurkish ? 'Ücretsiz' : 'Free') : `₺${deliveryFee.toFixed(2)}`}
                        </span>
                      </div>
                      {deliveryFee > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {isTurkish 
                            ? `₺${(100 - cartTotal).toFixed(2)} daha ekleyin, kargo bedava!` 
                            : `Add ₺${(100 - cartTotal).toFixed(2)} more for free delivery!`}
                        </p>
                      )}
                      <Separator />
                      <div className="flex justify-between font-semibold">
                        <span>{isTurkish ? 'Toplam' : 'Total'}</span>
                        <span className="text-emerald-600">₺{orderTotal.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <SheetFooter>
                      <Button 
                        className="w-full rounded-full" 
                        size="lg"
                        onClick={() => setCheckoutOpen(true)}
                      >
                        {isTurkish ? 'Siparişi Tamamla' : 'Checkout'}
                      </Button>
                    </SheetFooter>
                  </>
                )}
              </SheetContent>
            </Sheet>
          </div>
          
          {/* Category Pills */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              className="rounded-full shrink-0"
              onClick={() => setSelectedCategory('all')}
            >
              {isTurkish ? 'Tümü' : 'All'}
            </Button>
            {availableCategories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                className="rounded-full shrink-0"
                onClick={() => setSelectedCategory(cat)}
              >
                {categoryNames[cat] || cat}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          {products.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {isTurkish ? 'Bu mağazada henüz ürün yok' : 'No products in this store yet'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {products.map((product) => {
                const inCart = cart.find(item => item.id === product.id);
                
                return (
                  <Card 
                    key={product.id} 
                    className="group overflow-hidden hover:shadow-lg transition-shadow"
                    data-testid={`product-${product.id}`}
                  >
                    <CardContent className="p-3">
                      {/* Product Image */}
                      <div className="aspect-square rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center mb-3 relative overflow-hidden">
                        <span className="text-3xl font-bold text-emerald-300">
                          {product.brand?.charAt(0) || 'P'}
                        </span>
                        {product.discount_percentage > 0 && (
                          <Badge className="absolute top-1 left-1 bg-red-500 text-xs">
                            -{product.discount_percentage}%
                          </Badge>
                        )}
                      </div>
                      
                      {/* Brand */}
                      <Badge variant="secondary" className="mb-1 text-xs">
                        {product.brand}
                      </Badge>
                      
                      {/* Name */}
                      <h3 className="font-medium text-sm line-clamp-2 mb-1 min-h-[40px]">
                        {product.name}
                      </h3>
                      
                      {/* Weight */}
                      {product.weight && (
                        <p className="text-xs text-muted-foreground mb-2">{product.weight}</p>
                      )}
                      
                      {/* Price & Add Button */}
                      <div className="flex items-center justify-between mt-auto">
                        <span className="font-bold text-emerald-600">
                          ₺{product.price?.toFixed(2)}
                        </span>
                        
                        {inCart ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateCartQuantity(product.id, -1)}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-medium">
                              {inCart.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateCartQuantity(product.id, 1)}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            className="rounded-full h-8"
                            onClick={() => addToCart(product)}
                            disabled={product.stock <= 0}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      {product.stock <= 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          {isTurkish ? 'Stokta yok' : 'Out of stock'}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              {isTurkish ? 'Teslimat Bilgileri' : 'Delivery Information'}
            </DialogTitle>
            <DialogDescription>
              {isTurkish ? 'Siparişinizi tamamlamak için teslimat bilgilerinizi girin.' : 'Enter your delivery details to complete your order.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isTurkish ? 'Ad Soyad' : 'Full Name'} *</Label>
              <Input
                value={checkoutForm.customer_name}
                onChange={(e) => setCheckoutForm(prev => ({ ...prev, customer_name: e.target.value }))}
                placeholder={isTurkish ? 'Adınız Soyadınız' : 'Your full name'}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isTurkish ? 'Telefon' : 'Phone'} *</Label>
              <Input
                value={checkoutForm.customer_phone}
                onChange={(e) => setCheckoutForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                placeholder="+90 5XX XXX XX XX"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isTurkish ? 'E-posta' : 'Email'}</Label>
              <Input
                type="email"
                value={checkoutForm.customer_email}
                onChange={(e) => setCheckoutForm(prev => ({ ...prev, customer_email: e.target.value }))}
                placeholder="email@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isTurkish ? 'Teslimat Adresi' : 'Delivery Address'} *</Label>
              <Textarea
                value={checkoutForm.delivery_address}
                onChange={(e) => setCheckoutForm(prev => ({ ...prev, delivery_address: e.target.value }))}
                placeholder={isTurkish ? 'Tam adresinizi yazın' : 'Enter your full address'}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isTurkish ? 'Sipariş Notu' : 'Order Notes'}</Label>
              <Textarea
                value={checkoutForm.notes}
                onChange={(e) => setCheckoutForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={isTurkish ? 'Özel istekleriniz (opsiyonel)' : 'Special requests (optional)'}
                rows={2}
              />
            </div>
            
            <Separator />
            
            <div className="bg-secondary/50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span>{isTurkish ? 'Ürünler' : 'Products'}</span>
                <span>₺{cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>{isTurkish ? 'Teslimat' : 'Delivery'}</span>
                <span className={deliveryFee === 0 ? 'text-green-600' : ''}>
                  {deliveryFee === 0 ? (isTurkish ? 'Ücretsiz' : 'Free') : `₺${deliveryFee}`}
                </span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold">
                <span>{isTurkish ? 'Toplam' : 'Total'}</span>
                <span className="text-emerald-600">₺{orderTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutOpen(false)}>
              {isTurkish ? 'İptal' : 'Cancel'}
            </Button>
            <Button onClick={handleCheckout} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isTurkish ? 'Siparişi Onayla' : 'Confirm Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupermarketStorePage;
